import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { useGetAllExercises } from "../../service/api";
import { ExerciseRow } from "../../types";

const DIALOG_PAPER_SX = { backgroundColor: "#fff", color: "#000" };

type PlanExercisePickerDialogProps = {
  open: boolean;
  initialSelectedIds: string[];
  onClose: () => void;
  onConfirm: (selectedIds: string[]) => void;
};

export const PlanExercisePickerDialog = ({
  open,
  initialSelectedIds,
  onClose,
  onConfirm,
}: PlanExercisePickerDialogProps) => {
  const { t } = useTranslation();
  const { data: exercises = [] } = useGetAllExercises();
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(initialSelectedIds));
      setSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const visibleExercises = useMemo(
    () => exercises.filter((ex: ExerciseRow) => !ex.isDeleted && ex.title.toLowerCase().includes(search.toLowerCase())),
    [exercises, search]
  );

  const totalDuration = useMemo(
    () => Array.from(selectedIds).reduce((sum, id) => sum + (exercises.find((ex) => ex.id === id)?.durationInSeconds ?? 0), 0),
    [selectedIds, exercises]
  );

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" slotProps={{ paper: { sx: DIALOG_PAPER_SX } }}>
      <DialogTitle sx={{ color: "#000" }}>{t("videoLibrary.planPicker.title")}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1, pt: "12px !important" }}>
        <TextField
          placeholder={t("videoLibrary.planPicker.searchPlaceholder")}
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
        />
        <Divider />
        <List sx={{ maxHeight: 320, overflow: "auto" }} dense>
          {visibleExercises.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1, px: 1 }}>{t("videoLibrary.planPicker.empty")}</Typography>
          ) : (
            visibleExercises.map((ex: ExerciseRow) => (
              <ListItemButton key={ex.id} onClick={() => toggle(ex.id)} dense>
                <Checkbox edge="start" checked={selectedIds.has(ex.id)} size="small" tabIndex={-1} disableRipple />
                <ListItemText primary={ex.title} secondary={`${ex.durationInSeconds}s`} />
              </ListItemButton>
            ))
          )}
        </List>
        <Divider />
        <Typography variant="body2" color="text.secondary">
          {t("videoLibrary.planPicker.selectedTotal", { count: selectedIds.size, duration: totalDuration })}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("videoLibrary.common.cancel")}</Button>
        <Button variant="contained" onClick={() => onConfirm(Array.from(selectedIds))} sx={{ color: "#fff" }}>
          {t("videoLibrary.common.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
